import { useState } from "react";
import type { ReactNode } from "react";

/**
 * The shared shell for "create X" buttons: a primary button that opens a dialog,
 * and a short-lived confirmation beside it after a create. NewRequestButton and
 * AddAccountButton were identical here — this is that shell, once.
 *
 * `children` is a render prop given `close` and `create`: the dialog calls
 * `create(result)` on submit, which closes, shows the confirmation, and hands
 * the result to `onResult` (where the caller POSTs and revalidates).
 */
export function CreateEntityButton<T>({
  label,
  buildConfirmation,
  onResult,
  children,
}: {
  label: string;
  buildConfirmation: (result: T) => string;
  onResult?: (result: T) => void;
  children: (close: () => void, create: (result: T) => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  return (
    <>
      <div className="flex items-center gap-3">
        {confirmation && (
          <span role="status" className="text-xs text-success-600">
            {confirmation}
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:focus-ring"
        >
          {label}
        </button>
      </div>

      {open &&
        children(
          () => setOpen(false),
          (result) => {
            setOpen(false);
            setConfirmation(buildConfirmation(result));
            onResult?.(result);
            window.setTimeout(() => setConfirmation(null), 4000);
          },
        )}
    </>
  );
}
